import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { Icono } from './Icono';
import { SelectorEmoji } from './SelectorEmoji';
import { enfocarAlMontar } from './tactil';
import { color, radio } from './tokens';

type CampoNombreEmojiProps = {
  emoji: string | null;
  onEmoji: (emoji: string | null) => void;
  nombre: string;
  onNombre: (nombre: string) => void;
  'aria-label': string;
  placeholder?: string;
  /** Intro en el campo del nombre. */
  onEnter?: () => void;
  /** Con el teclado abierto al montar. */
  enfocar?: boolean;
  'data-testid'?: string;
};

/**
 * Nombre de una categoría o grupo con su emoji a la izquierda: tocar el
 * emoji abre `SelectorEmoji`. Quien lo usa guarda `unirEmoji(emoji, nombre)`.
 */
export function CampoNombreEmoji({
  emoji,
  onEmoji,
  nombre,
  onNombre,
  'aria-label': ariaLabel,
  placeholder,
  onEnter,
  enfocar = true,
  'data-testid': testId,
}: CampoNombreEmojiProps) {
  const { t } = useTranslation();
  const [eligiendo, setEligiendo] = useState(false);

  return (
    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'stretch' }}>
      <button
        type="button"
        data-testid="campo-emoji"
        aria-label={emoji ? t('Change emoji') : t('Choose an emoji')}
        onClick={() => setEligiendo(true)}
        className={css({
          width: 52,
          height: 52,
          flexShrink: 0,
          borderRadius: radio.boton,
          border: `1px solid ${color.line2}`,
          backgroundColor: color.surface2,
          color: color.fg3,
          fontSize: 26,
          lineHeight: 1,
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
          padding: 0,
        })}
      >
        {emoji ?? <Icono nombre="plus" size={20} />}
      </button>
      <input
        ref={enfocar ? enfocarAlMontar : undefined}
        aria-label={ariaLabel}
        data-testid={testId}
        placeholder={placeholder}
        value={nombre}
        // Sin la barra «Autorrellenar contacto» del iPhone: es el nombre de
        // una categoría, no de una persona.
        autoComplete="off"
        enterKeyHint="done"
        onChange={e => onNombre(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            onEnter?.();
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          height: 52,
          borderRadius: radio.boton,
          border: `2px solid ${color.accent}`,
          padding: '0 14px',
          fontSize: 17,
          fontWeight: 700,
          fontFamily: 'inherit',
          color: color.fg,
          backgroundColor: color.surface2,
          outline: 'none',
        }}
      />
      {eligiendo && (
        <SelectorEmoji
          actual={emoji}
          onElegir={nuevo => {
            onEmoji(nuevo);
            setEligiendo(false);
          }}
          onCerrar={() => setEligiendo(false)}
        />
      )}
    </View>
  );
}
